import { z } from 'zod';

export const unlockRuleSchema = z
  .object({
    targetId: z.string().trim().min(1),
    touched: z.number().int().nonnegative(),
    everCorrect: z.number().int().nonnegative(),
  })
  .strict()
  .refine((value) => value.everCorrect <= value.touched, {
    message: 'everCorrect must not exceed touched',
  });

export const unlockRuleListSchema = z.array(unlockRuleSchema);
export type UnlockRule = z.infer<typeof unlockRuleSchema>;

export type ProgressSection = {
  id: string;
  unlocks?: readonly UnlockRule[] | null;
};
export type ProgressUnit = {
  id: string;
  sectionId: string;
  unlocks?: readonly UnlockRule[] | null;
};
export type ProgressTopic = {
  id: string;
  unitId: string;
  unlocks?: readonly UnlockRule[] | null;
};
export type ProgressCard = { id: string; topicId: string; active: boolean };

type ProgressInput = {
  sections: readonly ProgressSection[];
  units: readonly ProgressUnit[];
  topics: readonly ProgressTopic[];
  cards: readonly ProgressCard[];
  events: readonly { cardId: string; result: string }[];
};

type Entity = {
  id: string;
  kind: 'section' | 'unit' | 'topic';
  parentId?: string;
  unlocks: readonly UnlockRule[];
};

type NormalizedInput = {
  entities: Entity[];
  entityById: Map<string, Entity>;
  cardTopic: Map<string, string>;
  activeCards: Set<string>;
};

export type DerivedUnlockProgress = {
  unlocked: Set<string>;
  isEntityUnlocked(id: string): boolean;
  isCardUnlocked(id: string): boolean;
};

function normalizeInput(input: ProgressInput): NormalizedInput {
  const entities: Entity[] = [
    ...input.sections.map((item) => ({
      id: item.id,
      kind: 'section' as const,
      unlocks: item.unlocks ?? [],
    })),
    ...input.units.map((item) => ({
      id: item.id,
      kind: 'unit' as const,
      parentId: item.sectionId,
      unlocks: item.unlocks ?? [],
    })),
    ...input.topics.map((item) => ({
      id: item.id,
      kind: 'topic' as const,
      parentId: item.unitId,
      unlocks: item.unlocks ?? [],
    })),
  ];
  const entityById = new Map(entities.map((item) => [item.id, item]));
  const cardTopic = new Map<string, string>();
  const activeCards = new Set<string>();
  for (const card of input.cards) {
    if (
      !entityById.has(card.topicId) ||
      entityById.get(card.topicId)?.kind !== 'topic'
    )
      throw new Error('Card references an unknown topic');
    cardTopic.set(card.id, card.topicId);
    if (card.active) activeCards.add(card.id);
  }
  for (const entity of entities) {
    if (entity.parentId && !entityById.has(entity.parentId))
      throw new Error('Hierarchy references an unknown parent');
    for (const rule of entity.unlocks) unlockRuleSchema.parse(rule);
  }
  return { entities, entityById, cardTopic, activeCards };
}

function ancestors(normalized: NormalizedInput, entityId: string) {
  const ids = new Set<string>();
  let current: string | undefined = entityId;
  while (current) {
    ids.add(current);
    current = normalized.entityById.get(current)?.parentId;
  }
  return ids;
}

function resolveRuleTarget(
  source: Entity,
  targetId: string,
  normalized: NormalizedInput,
) {
  const matches = normalized.entities.filter(
    (candidate) =>
      candidate.id === targetId &&
      candidate.kind === source.kind &&
      candidate.parentId === source.parentId,
  );
  if (matches.length === 0)
    throw new Error(`Unlock target does not exist: ${targetId}`);
  if (matches.length > 1)
    throw new Error(`Unlock target is ambiguous: ${targetId}`);
  if (matches[0]!.id === source.id)
    throw new Error(`Unlock target cannot reference itself: ${targetId}`);
  return matches[0]!;
}

function validateGraph(normalized: NormalizedInput) {
  const incoming = new Map<string, string>();
  const edges = new Map<string, string[]>();
  for (const source of normalized.entities) {
    for (const rule of source.unlocks) {
      const target = resolveRuleTarget(source, rule.targetId, normalized);
      if (incoming.has(target.id))
        throw new Error(
          `Unlock target has duplicate incoming rules: ${rule.targetId}`,
        );
      incoming.set(target.id, source.id);
      edges.set(source.id, [...(edges.get(source.id) ?? []), target.id]);
    }
  }
  const visiting = new Set<string>();
  const visited = new Set<string>();
  const visit = (id: string) => {
    if (visiting.has(id)) throw new Error('Unlock rules contain a cycle');
    if (visited.has(id)) return;
    visiting.add(id);
    for (const child of edges.get(id) ?? []) visit(child);
    visiting.delete(id);
    visited.add(id);
  };
  for (const entity of normalized.entities) visit(entity.id);
  return { incoming, edges };
}

/** Derive entity/card availability from sibling unlock rules and review history. */
export function deriveUnlockProgress(
  input: ProgressInput,
): DerivedUnlockProgress {
  const normalized = normalizeInput(input);
  const { incoming } = validateGraph(normalized);
  const touched = new Set<string>();
  const everCorrect = new Set<string>();
  const cardsByEntity = new Map<string, Set<string>>();
  for (const cardId of normalized.activeCards) {
    const topicId = normalized.cardTopic.get(cardId)!;
    for (const entityId of ancestors(normalized, topicId)) {
      const cards = cardsByEntity.get(entityId) ?? new Set<string>();
      cards.add(cardId);
      cardsByEntity.set(entityId, cards);
    }
  }
  for (const event of input.events) {
    if (!normalized.activeCards.has(event.cardId)) continue;
    touched.add(event.cardId);
    if (event.result === 'correct') everCorrect.add(event.cardId);
  }
  for (const source of normalized.entities) {
    const sourceCards = cardsByEntity.get(source.id) ?? new Set<string>();
    for (const rule of source.unlocks) {
      if (
        sourceCards.size === 0 ||
        rule.touched > sourceCards.size ||
        rule.everCorrect > sourceCards.size
      )
        throw new Error(
          `Unlock requirement exceeds active cards in source subtree: ${source.id}`,
        );
    }
  }
  const unlocked = new Set<string>();
  const pending = new Set(normalized.entities.map((entity) => entity.id));
  while (pending.size) {
    let changed = false;
    for (const entityId of pending) {
      const entity = normalized.entityById.get(entityId)!;
      if (entity.parentId && !unlocked.has(entity.parentId)) continue;
      const sourceId = incoming.get(entity.id);
      if (sourceId) {
        if (!unlocked.has(sourceId)) continue;
        const source = normalized.entityById.get(sourceId)!;
        const rule = source.unlocks.find(
          (candidate) =>
            resolveRuleTarget(source, candidate.targetId, normalized).id ===
            entity.id,
        )!;
        const sourceCards = cardsByEntity.get(source.id) ?? new Set<string>();
        const sourceTouched = [...sourceCards].filter((id) =>
          touched.has(id),
        ).length;
        const sourceCorrect = [...sourceCards].filter((id) =>
          everCorrect.has(id),
        ).length;
        if (sourceTouched < rule.touched || sourceCorrect < rule.everCorrect)
          continue;
      }
      unlocked.add(entity.id);
      pending.delete(entity.id);
      changed = true;
    }
    if (!changed) break;
  }
  return {
    unlocked,
    isEntityUnlocked: (id) => unlocked.has(id),
    isCardUnlocked: (id) => {
      const topicId = normalized.cardTopic.get(id);
      return topicId !== undefined && unlocked.has(topicId);
    },
  };
}
